import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import NotificationDropdown from './NotificationDropdown';
import { getCurrentUser, getCachedUser, isAuthenticated, signOut } from '../utils/auth';
import { supabase } from '../utils/supabaseClient';
import { getBalance } from '../utils/balance';
import { getUnreadMessageCount } from '../utils/messageCount';
import {
  ShoppingCart,
  Tag,
  TrendingUp,
  Crown,
  Wallet,
  FileText,
  MessageSquare,
  Bell,
  Star,
  Settings,
  User,
  ChevronDown,
  ChevronRight,
  X
} from 'lucide-react';
import '../styles/UserMenu.css';

const DASHBOARD_ICONS = {
  orders: '/main/dashboardicons/orders.svg',
  offers: '/main/dashboardicons/offers.svg',
  seller: '/main/dashboardicons/seller.svg',
  boosting: '/main/dashboardicons/boosting.svg',
  wallet: '/main/dashboardicons/wallet.svg',
  message: '/main/dashboardicons/message.svg',
  bell: '/main/dashboardicons/bell.svg',
  feedback: '/main/dashboardicons/feedback.svg',
  profile: '/main/dashboardicons/profile.svg',
};

/* User menu header icons (UMI) – used in .user-menu-icons */
const UMI_ICONS = {
  bell: '/main/dashboardicons/umi/bell-umi.svg',
  cart: '/main/dashboardicons/umi/cart-umi.svg',
  message: '/main/dashboardicons/umi/message-uni.svg',
};

/* User menu dropdown icons (UMD) – used in .user-menu-dropdown */
const UMD_ICONS = {
  message: '/main/dashboardicons/umd/message.svg',
  bell: '/main/dashboardicons/umd/bell.svg',
  wallet: '/main/dashboardicons/umd/wallet.svg',
  settings: '/main/dashboardicons/umd/settings.svg',
  theme: '/main/dashboardicons/umd/theme.svg',
  logout: '/main/dashboardicons/umd/logout.svg',
};

const UserMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Initialize with cached user data for instant display
  const cachedUser = getCachedUser();
  // Use cached user immediately - don't wait for state updates
  const [user, setUser] = useState(cachedUser);
  const [loading, setLoading] = useState(false); // Never show loading if we have cached data
  const [showDropdown, setShowDropdown] = useState(false);
  const [balance, setBalance] = useState(null);
  const dropdownRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 968);
  const [expandedItems, setExpandedItems] = useState({
    orders: false,
    offers: false,
    seller: false,
    boosting: false,
  });

  // Update expanded state based on active tab
  useEffect(() => {
    const path = location.pathname;
    if (path === '/profile/orders/purchased' || path === '/profile/orders/sold') {
      setExpandedItems(prev => ({ ...prev, orders: true }));
    }
    if (path?.includes('/profile/offers/')) {
      setExpandedItems(prev => ({ ...prev, offers: true }));
    }
    if (path === '/profile/boosting/my-requests' || path === '/profile/boosting/received') {
      setExpandedItems(prev => ({ ...prev, boosting: true }));
    }
  }, [location.pathname]);
  
  // Listen for login events immediately to update user state
  useEffect(() => {
    const handleLogin = () => {
      const newCachedUser = getCachedUser();
      if (newCachedUser) {
        setUser(newCachedUser);
        setLoading(false);
      }
    };
    
    window.addEventListener('user-login', handleLogin);
    
    return () => {
      window.removeEventListener('user-login', handleLogin);
    };
  }, []);

  // Use cached user ID as dependency instead of whole object to prevent infinite loops
  const cachedUserId = cachedUser?.id;

  useEffect(() => {
    // If we have cached user, use it immediately - don't wait for API
    if (cachedUser) {
      setUser(cachedUser);
      setLoading(false);
    }

    // Only load user if authenticated and we don't have cached data
    // This prevents infinite loops
    if (!isAuthenticated()) {
      setLoading(false);
      return;
    }

    // Use a ref to prevent multiple simultaneous calls
    let cancelled = false;
    
    const loadUser = async () => {
      try {
        // Fetch fresh user data in background (non-blocking)
        const { user: currentUser, error } = await getCurrentUser();
        if (cancelled) return;
        
        if (!error && currentUser) {
          setUser(currentUser);
        } else if (error) {
          // If 401, token expired - don't clear cached data immediately
          // User can still see cached UI, just won't get fresh data
          console.warn('Failed to refresh user data:', error.message);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error loading user:', err);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    // Load fresh data in background, but don't block UI
    loadUser();
    
    // Subscribe to real-time user updates (avatar changes and seller status)
    let userChannel = null;
    const handleLogin = () => {
      const newCachedUser = getCachedUser();
      if (newCachedUser) {
        setUser(newCachedUser);
        setLoading(false);
      }
    };
    
    window.addEventListener('user-login', handleLogin);
    
    if (cachedUserId) {
      userChannel = supabase
        .channel(`user_menu_updates:${cachedUserId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'users',
            filter: `id=eq.${cachedUserId}`,
          },
          (payload) => {
            if (payload.new) {
              setUser(prev => ({ ...prev, ...payload.new }));
              // Update cached user data properly (individual localStorage items)
              if (typeof localStorage !== 'undefined') {
                if (payload.new.id) localStorage.setItem('user_id', payload.new.id);
                if (payload.new.email !== undefined) localStorage.setItem('user_email', payload.new.email || '');
                if (payload.new.username !== undefined) localStorage.setItem('user_username', payload.new.username || '');
                if (payload.new.avatar_url !== undefined) localStorage.setItem('user_avatar_url', payload.new.avatar_url || '');
                if (payload.new.is_seller !== undefined) localStorage.setItem('user_is_seller', payload.new.is_seller ? 'true' : 'false');
                if (payload.new.veriff_status !== undefined) localStorage.setItem('user_veriff_status', payload.new.veriff_status || '');
              }
              // Update cached avatar
              if (payload.new.avatar_url && !payload.new.avatar_url.startsWith('letter:')) {
                if (typeof localStorage !== 'undefined') {
                  localStorage.setItem(`avatar_${cachedUserId}`, payload.new.avatar_url);
                }
              } else {
                // Clear cached avatar if removed
                if (typeof localStorage !== 'undefined') {
                  localStorage.removeItem(`avatar_${cachedUserId}`);
                }
              }
            }
          }
        )
        .subscribe();
    }
    
    return () => {
      cancelled = true;
      window.removeEventListener('user-login', handleLogin);
      if (userChannel) {
        supabase.removeChannel(userChannel);
      }
    };
  }, [cachedUserId]); // Use cachedUserId instead of cachedUser to prevent infinite loops

  // Use cached user if available, even if state hasn't updated yet
  const displayUser = user || cachedUser;

  // Load balance with caching (instant display)
  useEffect(() => {
    if (isAuthenticated() && displayUser) {
      // Load from cache immediately
      const cachedBalance = (() => {
        try {
          const cached = localStorage.getItem('user_balance');
          if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed.timestamp && Date.now() - parsed.timestamp < 5 * 60 * 1000) {
              return parsed.data;
            }
          }
        } catch (e) {
          // Ignore
        }
        return null;
      })();
      
      if (cachedBalance) {
        setBalance(cachedBalance.data || cachedBalance);
      }
      
      // Sync with API in background
      getBalance(true).then(balanceData => {
        setBalance(balanceData.data || balanceData);
      }).catch(error => {
        // Silently handle timeout errors - cached data is already displayed
        // Only log non-timeout errors
        if (!error.message || !error.message.includes('timeout')) {
          console.error('Failed to load balance:', error);
        }
        // Keep cached data if API fails
      });
    }
  }, [displayUser]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 968);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Determine active tab from URL
  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/profile/account' || path === '/profile') return 'account';
    if (path === '/profile/wallet') return 'wallet';
    if (path === '/profile/messages') return 'messages';
    if (path === '/profile/notifications') return 'notifications';
    if (path === '/profile/seller') return 'seller';
    if (path === '/profile/offers/currency') return 'offers-currency';
    if (path === '/profile/offers/accounts') return 'offers-accounts';
    if (path === '/profile/offers/top-ups') return 'offers-topups';
    if (path === '/profile/offers/items') return 'offers-items';
    if (path === '/profile/offers') return 'offers-currency';
    if (path === '/profile/seller/purchased-orders') return 'seller-purchased-orders';
    if (path === '/profile/seller/sold-orders') return 'seller-sold-orders';
    if (path === '/profile/seller/my-items') return 'seller-my-items';
    if (path === '/profile/seller/requests') return 'seller-requests';
    if (path === '/profile/seller') return 'seller-purchased-orders';
    if (path === '/profile/boosting/my-requests') return 'my-boosting-requests';
    if (path === '/profile/boosting/received') return 'received-boosting-requests';
    if (path === '/profile/boosting') return 'received-boosting-requests';
    if (path === '/profile/feedback') return 'feedback';
    if (path === '/profile/view') return 'profile';
    if (path === '/profile/orders/purchased') return 'purchased-orders';
    if (path === '/profile/orders/sold') return 'sold-orders';
    if (path === '/profile/orders') return 'purchased-orders';
    return null;
  };

  const activeTab = getActiveTab();

  // Update expanded state when activeTab changes
  useEffect(() => {
    if (activeTab === 'purchased-orders' || activeTab === 'sold-orders') {
      setExpandedItems(prev => ({ ...prev, orders: true }));
    }
    if (activeTab === 'my-boosting-requests' || activeTab === 'received-boosting-requests') {
      setExpandedItems(prev => ({ ...prev, boosting: true }));
    }
  }, [activeTab]);

  const formatRegistrationDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'numeric', 
      day: 'numeric', 
      year: '2-digit' 
    });
  };

  const navItems = [
    { id: 'orders', label: 'Orders', icon: ShoppingCart, iconPath: DASHBOARD_ICONS.orders, hasDropdown: true, path: '/profile/orders', subItems: [
      { id: 'purchased-orders', label: 'Purchased orders', path: '/profile/orders/purchased' },
      { id: 'sold-orders', label: 'Sold orders', path: '/profile/orders/sold' },
    ]},
    { id: 'offers', label: 'Offers', icon: Tag, iconPath: DASHBOARD_ICONS.offers, hasDropdown: true, path: '/profile/offers', subItems: [
      { id: 'offers-currency', label: 'Currency', path: '/profile/offers/currency' },
      { id: 'offers-accounts', label: 'Accounts', path: '/profile/offers/accounts' },
      { id: 'offers-topups', label: 'Top Ups', path: '/profile/offers/top-ups' },
      { id: 'offers-items', label: 'Items', path: '/profile/offers/items' },
    ]},
    { id: 'seller', label: 'Seller', icon: FileText, iconPath: DASHBOARD_ICONS.seller, hasDropdown: true, path: '/profile/seller', subItems: [
      { id: 'become-seller', label: 'Become a Seller', path: '/profile/become-seller' },
      { id: 'seller-purchased-orders', label: 'Purchased Orders', path: '/profile/seller/purchased-orders' },
      { id: 'seller-sold-orders', label: 'Sold Orders', path: '/profile/seller/sold-orders' },
      { id: 'seller-my-items', label: 'My Items', path: '/profile/seller/my-items' },
      { id: 'seller-requests', label: 'Requests', path: '/profile/seller/requests' },
    ]},
    { id: 'boosting', label: 'Boosting', icon: TrendingUp, iconPath: DASHBOARD_ICONS.boosting, hasDropdown: true, path: '/profile/boosting', subItems: [
      { id: 'my-boosting-requests', label: 'My requests', path: '/profile/boosting/my-requests' },
      { id: 'received-boosting-requests', label: 'Received requests', path: '/profile/boosting/received' },
    ]},
    { id: 'loyalty', label: 'Loyalty', icon: Crown, badge: 'BETA', hasDropdown: false, path: '/profile/loyalty' },
    { id: 'wallet', label: 'Wallet', icon: Wallet, iconPath: DASHBOARD_ICONS.wallet, hasDropdown: false, path: '/profile/wallet' },
    { id: 'become-seller', label: 'Become a Seller', icon: FileText, iconPath: DASHBOARD_ICONS.seller, hasDropdown: false, path: '/profile/become-seller' },
    { id: 'messages', label: 'Messages', icon: MessageSquare, iconPath: DASHBOARD_ICONS.message, hasDropdown: false, path: '/profile/messages' },
    { id: 'notifications', label: 'Notifications', icon: Bell, iconPath: DASHBOARD_ICONS.bell, hasDropdown: false, path: '/profile/notifications' },
    { id: 'feedback', label: 'Feedback', icon: Star, iconPath: DASHBOARD_ICONS.feedback, hasDropdown: false, path: '/profile/feedback' },
    { id: 'account', label: 'Account settings', icon: Settings, iconPath: DASHBOARD_ICONS.profile, hasDropdown: false, path: '/profile/account' },
  ];

  const toggleDropdown = (itemId) => {
    if (itemId === 'orders' || itemId === 'offers' || itemId === 'seller' || itemId === 'boosting') {
      setExpandedItems(prev => ({
        ...prev,
        [itemId]: !prev[itemId]
      }));
    }
  };

  const handleNavClick = (item, subItem = null) => {
    if (item.hasDropdown && !subItem) {
      toggleDropdown(item.id);
      return;
    }
    
    if (subItem) {
      navigate(subItem.path);
    } else if (item.path) {
      navigate(item.path);
    }
    
    setShowDropdown(false);
  };

  // Lock body scroll when mobile modal is open
  useEffect(() => {
    if (isMobile && showDropdown) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isMobile, showDropdown]);

  // Close dropdown when clicking outside (desktop only)
  useEffect(() => {
    if (isMobile) return; // Don't close on outside click for mobile
    
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown, isMobile]);

  // Close profile menu when notifications dropdown opens
  useEffect(() => {
    const handleNotificationsOpened = () => {
      setShowDropdown(false);
    };

    window.addEventListener('notifications-dropdown-opened', handleNotificationsOpened);
    return () => {
      window.removeEventListener('notifications-dropdown-opened', handleNotificationsOpened);
    };
  }, []);

  const getAvatarDisplay = () => {
    // Use displayUser (cached or state) for immediate display
    const currentUser = displayUser || cachedUser;
    // Check localStorage first for immediate display
    const cachedAvatar = currentUser?.id ? localStorage.getItem(`avatar_${currentUser.id}`) : null;
    
    if (cachedAvatar) {
      return (
        <img 
          src={cachedAvatar} 
          alt={user.username || 'User'} 
          className="user-avatar-image"
        />
      );
    }
    
    if (currentUser?.avatar_url) {
      // Check if it's a letter avatar
      if (currentUser.avatar_url.startsWith('letter:')) {
        const letter = currentUser.avatar_url.split(':')[1];
        return (
          <div className="user-avatar-letter">
            {letter}
          </div>
        );
      }
      // Regular image avatar
      return (
        <img 
          src={currentUser.avatar_url} 
          alt={currentUser.username || 'User'} 
          className="user-avatar-image"
        />
      );
    }
    // Default letter avatar
    const letter = currentUser?.username?.charAt(0).toUpperCase() || 'U';
    return (
      <div className="user-avatar-letter">
        {letter}
      </div>
    );
  };

  // Show nothing only if we're truly loading AND have no cached data
  // If we have cached data, show it immediately even if not authenticated yet
  if (loading && !cachedUser) {
    return null;
  }
  
  // If not authenticated and no cached user, don't show menu
  if (!isAuthenticated() && !cachedUser) {
    return null;
  }
  
  if (!displayUser) {
    return null;
  }

  // Check seller status from both state and cache for instant display
  const isSeller = displayUser?.is_seller || cachedUser?.is_seller || user?.is_seller;

  const handleLogout = async () => {
    await signOut();
    setShowDropdown(false);
    navigate('/');
    window.location.reload();
  };

  const handleDropdownToggle = () => {
    const nextOpen = !showDropdown;
    setShowDropdown(nextOpen);
    if (nextOpen) {
      window.dispatchEvent(new CustomEvent('user-menu-opened'));
    }
  };

  // Messages button with unread count badge
  const MessagesButtonWithBadge = () => {
    const [unreadCount, setUnreadCount] = useState(0);

    useEffect(() => {
      if (!isAuthenticated()) return;

      const loadUnreadCount = async () => {
        const count = await getUnreadMessageCount();
        setUnreadCount(count);
      };

      loadUnreadCount();

      // Subscribe to message updates
      const channel = supabase
        .channel('user_messages')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages',
            filter: `receiver_id=eq.${cachedUser?.id || user?.id}`,
          },
          () => {
            // Reload unread count when new message arrives
            loadUnreadCount();
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'messages',
            filter: `receiver_id=eq.${cachedUser?.id || user?.id}`,
          },
          () => {
            // Reload when messages are marked as read
            loadUnreadCount();
          }
        )
        .subscribe();

      // Poll for updates every 10 seconds as backup
      const interval = setInterval(loadUnreadCount, 10000);

      return () => {
        supabase.removeChannel(channel);
        clearInterval(interval);
      };
    }, [cachedUser?.id, user?.id]);

    return (
      <button 
        className="user-menu-icon-btn messages-btn" 
        title="Messages"
        onClick={() => navigate('/messages')}
        style={{ position: 'relative' }}
      >
        <span
          className="user-menu-header-icon"
          style={{
            WebkitMaskImage: `url(${UMI_ICONS.message})`,
            maskImage: `url(${UMI_ICONS.message})`,
          }}
          aria-hidden
        />
        {unreadCount > 0 && (
          <span className="message-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>
    );
  };

  return (
    <div className="user-menu-wrapper" ref={dropdownRef}>
      <div className="user-menu-icons">
        <MessagesButtonWithBadge />
        <NotificationDropdown />
        <button 
          className="user-menu-avatar-btn"
          title="Profile"
          onClick={handleDropdownToggle}
        >
          {getAvatarDisplay()}
          <svg 
            width="12" 
            height="12" 
            viewBox="0 0 12 12" 
            fill="none"
            className="user-menu-avatar-chevron"
            style={{
              position: 'absolute',
              bottom: '-2px',
              right: '-2px',
              background: 'var(--bg-tertiary)',
              borderRadius: '50%',
              padding: '2px',
            }}
          >
            <path 
              d={showDropdown ? "M6 9L3 6L6 3" : "M6 3L9 6L6 9"} 
              stroke="var(--accent-primary)" 
              strokeWidth="1.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {showDropdown && isMobile ? (
        createPortal(
          <div className="user-menu-mobile-overlay" onClick={() => setShowDropdown(false)}>
            <div className="user-menu-mobile-modal" onClick={(e) => e.stopPropagation()}>
              <div className="user-menu-mobile-header">
                <div className="user-menu-header">
                  <div className="user-menu-avatar-large">
                    {getAvatarDisplay()}
                  </div>
                  <div className="user-menu-info">
                    <div className="user-menu-username">{displayUser?.username || 'User'}</div>
                    <div className="user-menu-registered">
                      Registered: {formatRegistrationDate(displayUser?.created_at)}
                    </div>
                  </div>
                </div>
                <button 
                  className="user-menu-mobile-close"
                  onClick={() => setShowDropdown(false)}
                  aria-label="Close menu"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="user-menu-divider"></div>

              <nav className="user-menu-nav">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isExpanded = expandedItems[item.id] || false;
                  const isActive = activeTab === item.id || 
                    (item.subItems && item.subItems.some(sub => sub.id === activeTab));
                  const isParentActive = item.subItems && item.subItems.some(sub => sub.id === activeTab);
                  
                  return (
                    <div key={item.id} className="user-menu-nav-group">
                      <button
                        className={`user-menu-nav-item ${isActive && !item.subItems ? 'active' : ''} ${isParentActive ? 'parent-active' : ''}`}
                        onClick={() => handleNavClick(item)}
                      >
                        {item.iconPath ? (
                          <span
                            className="user-menu-nav-icon"
                            style={{
                              WebkitMaskImage: `url(${item.iconPath})`,
                              maskImage: `url(${item.iconPath})`,
                            }}
                          />
                        ) : (
                          <Icon size={18} className="user-menu-nav-icon" />
                        )}
                        <span className="user-menu-nav-label">{item.label}</span>
                        {item.badge && (
                          <span className="user-menu-nav-badge">{item.badge}</span>
                        )}
                        {item.hasDropdown && (
                          isExpanded ? (
                            <ChevronDown size={16} className="user-menu-nav-chevron" />
                          ) : (
                            <ChevronRight size={16} className="user-menu-nav-chevron" />
                          )
                        )}
                      </button>
                      {item.hasDropdown && item.subItems && isExpanded && (
                        <div className="user-menu-nav-subitems">
                          {item.subItems.map((subItem) => {
                            const isSubActive = activeTab === subItem.id;
                            return (
                              <button
                                key={subItem.id}
                                className={`user-menu-nav-subitem ${isSubActive ? 'active' : ''}`}
                                onClick={() => handleNavClick(item, subItem)}
                              >
                                {subItem.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
              <div className="user-menu-divider"></div>
              <button className="user-menu-item user-menu-logout" onClick={handleLogout}>
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path d="M13 3H16C17.1 3 18 3.9 18 5V15C18 16.1 17.1 17 16 17H13M8 13L13 8M13 8L8 3M13 8H3" stroke="#f87171" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Log out</span>
              </button>
            </div>
          </div>,
          document.body
        )
      ) : showDropdown ? (
        <div className="user-menu-dropdown">
          <div className="user-menu-profile-card">
            <div className="user-menu-avatar-wrap">
              {getAvatarDisplay()}
              <span className="user-menu-online-dot" aria-hidden />
            </div>
            <div className="user-menu-info">
              <div className="user-menu-username">{displayUser?.username || 'User'}</div>
              <div className="user-menu-user-id">ID: {displayUser?.id ? String(displayUser.id).slice(-6) : '—'}</div>
            </div>
          </div>

          <div className="user-menu-balance-block">
            <div className="user-menu-balance-row">
              <span className="user-menu-balance-label">Store Credit</span>
              <span className="user-menu-balance-value">
                €{balance != null ? parseFloat(balance.balance || 0).toFixed(2).replace('.', ',') : '0,00'}
              </span>
            </div>
          </div>

          {(() => {
            const { pathname, search } = location;
            const isOrdersActive = pathname.startsWith('/profile/orders') || (pathname === '/profile' && search.includes('tab=orders'));
            const isMessagesActive = pathname.startsWith('/profile/messages') || pathname.startsWith('/messages') || (pathname === '/profile' && search.includes('tab=messages'));
            const isOffersActive = pathname.startsWith('/profile/offers');
            const isNotificationsActive = pathname.startsWith('/profile/notifications') || (pathname === '/profile' && search.includes('tab=notifications'));
            const isWalletActive = pathname.startsWith('/profile/wallet');
            const isAccountActive = (pathname === '/profile' && !search.includes('tab=')) || pathname.startsWith('/profile/account') || pathname.startsWith('/profile/view');
            return (
              <nav className="user-menu-links" aria-label="Account">
                <Link to="/profile/orders/purchased" className={`user-menu-item user-menu-item-umd ${isOrdersActive ? 'active' : ''}`} onClick={() => setShowDropdown(false)}>
                  <span className="user-menu-item-icon" style={{ WebkitMaskImage: `url(${DASHBOARD_ICONS.orders})`, maskImage: `url(${DASHBOARD_ICONS.orders})` }} />
                  <span>Orders</span>
                </Link>
                <Link to="/profile/messages" className={`user-menu-item user-menu-item-umd ${isMessagesActive ? 'active' : ''}`} onClick={() => setShowDropdown(false)}>
                  <span className="user-menu-item-icon" style={{ WebkitMaskImage: `url(${UMD_ICONS.message})`, maskImage: `url(${UMD_ICONS.message})` }} />
                  <span>Messages</span>
                </Link>
                <Link to="/profile/offers/currency" className={`user-menu-item user-menu-item-umd ${isOffersActive ? 'active' : ''}`} onClick={() => setShowDropdown(false)}>
                  <span className="user-menu-item-icon" style={{ WebkitMaskImage: `url(${DASHBOARD_ICONS.offers})`, maskImage: `url(${DASHBOARD_ICONS.offers})` }} />
                  <span>Offers</span>
                </Link>
                <Link to="/profile/notifications" className={`user-menu-item user-menu-item-umd ${isNotificationsActive ? 'active' : ''}`} onClick={() => setShowDropdown(false)}>
                  <span className="user-menu-item-icon" style={{ WebkitMaskImage: `url(${UMD_ICONS.bell})`, maskImage: `url(${UMD_ICONS.bell})` }} />
                  <span>Notifications</span>
                </Link>
                <Link to="/profile/wallet" className={`user-menu-item user-menu-item-umd ${isWalletActive ? 'active' : ''}`} onClick={() => setShowDropdown(false)}>
                  <span className="user-menu-item-icon" style={{ WebkitMaskImage: `url(${UMD_ICONS.wallet})`, maskImage: `url(${UMD_ICONS.wallet})` }} />
                  <span>Wallet</span>
                </Link>
                <Link to="/profile/account" className={`user-menu-item user-menu-item-umd ${isAccountActive ? 'active' : ''}`} onClick={() => setShowDropdown(false)}>
                  <span className="user-menu-item-icon" style={{ WebkitMaskImage: `url(${UMD_ICONS.settings})`, maskImage: `url(${UMD_ICONS.settings})` }} />
                  <span>Settings</span>
                </Link>
              </nav>
            );
          })()}

          <div className="user-menu-divider" />

          <button type="button" className="user-menu-item user-menu-item-umd user-menu-logout" onClick={handleLogout}>
            <span className="user-menu-item-icon user-menu-logout-icon" style={{ WebkitMaskImage: `url(${UMD_ICONS.logout})`, maskImage: `url(${UMD_ICONS.logout})` }} />
            <span>Logout</span>
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default UserMenu;
